import { Link } from 'react-router-dom'
import { featuredModuleCards, moreModuleCards } from '@/data/modules'
import type { ModulePage } from '@/data/modules'
import ModuleIcon from '@/pages/product/ModuleIcon'

function MenuItem({ module, onNavigate }: { module: ModulePage; onNavigate?: () => void }) {
  return (
    <Link
      to={`/product/${module.slug}`}
      onClick={onNavigate}
      className="group/item flex items-center gap-3 rounded-lg px-3 py-2 no-underline transition-colors hover:bg-slate-50"
    >
      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-teal-50 text-accent-teal">
        <ModuleIcon name={module.icon} className="h-4 w-4" />
      </span>
      <span className="text-sm font-medium text-slate-700 transition-colors group-hover/item:text-slate-900">
        {module.name}
      </span>
    </Link>
  )
}

function ColumnHeading({ children }: { children: string }) {
  return (
    <p className="px-3 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-400">
      {children}
    </p>
  )
}

/**
 * Desktop "Product" nav item: a hover/focus dropdown listing every module
 * showcase page. The trigger itself links to the /product hub.
 */
export default function ProductMenu() {
  return (
    <div className="group relative">
      <Link
        to="/product"
        className="inline-flex items-center gap-1 text-sm text-slate-600 no-underline transition-colors hover:text-slate-900"
      >
        Product
        <svg
          className="h-3.5 w-3.5 text-slate-400 transition-transform duration-200 group-hover:rotate-180"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
        </svg>
      </Link>

      {/* Panel: pt-3 keeps the hover bridge so the mouse can cross the gap. */}
      <div className="invisible absolute right-0 top-full z-[110] pt-3 opacity-0 transition-all duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        <div className="w-[540px] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
          <div className="grid grid-cols-2 gap-x-4">
            <div>
              <ColumnHeading>Core modules</ColumnHeading>
              {featuredModuleCards.map((m) => (
                <MenuItem key={m.slug} module={m} />
              ))}
            </div>
            <div>
              <ColumnHeading>More modules</ColumnHeading>
              {moreModuleCards.map((m) => (
                <MenuItem key={m.slug} module={m} />
              ))}
            </div>
          </div>
          <Link
            to="/product"
            className="mt-2 flex items-center justify-center gap-1.5 border-t border-slate-100 pt-3 text-sm font-semibold text-accent-teal no-underline hover:text-accent-tealDark"
          >
            View all modules
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  )
}
